import axios, { AxiosInstance } from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { isBridgeActive, getBridgeToken, invalidateBridgeToken } from './auth/mpdTokenBridge';

export const myProviderDataInstance : AxiosInstance = axios.create({
  baseURL: "https://data-core.teampbs.com/MyProviderDataAPI/api",
  timeout: 120000,
});

export const MY_PROVIDER_DATA_SCOPES = ["api://f4426f66-0ecf-4cd4-99f2-e79950f3091a/access_as_user"];

// Helper function to check if error requires interaction
const isInteractionRequiredError = (error: any): boolean => {
  return error instanceof InteractionRequiredAuthError ||
         error.errorCode === "interaction_required" ||
         error.errorCode === "consent_required" ||
         error.errorCode === "login_required" ||
         error.errorCode === "token_expired";
};

export const initializeMyProviderDataService = (msalApp: PublicClientApplication) => {
  myProviderDataInstance.interceptors.request.use(async function (config) {
    // Hosts that bridge a token over postMessage (PBS Live desktop/mobile)
    // supply the bearer directly - MSAL is never consulted in that mode.
    if (isBridgeActive()) {
      const bridgeToken = await getBridgeToken();
      if (bridgeToken) {
        config.headers.Authorization = `Bearer ${bridgeToken}`;
        return config;
      }
      return Promise.reject(new Error("Host token bridge stopped answering - cannot authenticate MyProviderData request"));
    }

    if (!msalApp) throw new Error("MSAL not initialized");
    
    const activeAccount = msalApp.getActiveAccount();
    const accounts = msalApp.getAllAccounts();
    
    // Handle unauthenticated state
    if (!activeAccount && accounts.length === 0) {
      console.warn("No active MSAL account found for MyProviderData service. User is not authenticated.");
      
      // Check if authentication is already in progress
      try {
        const activeRequest = (msalApp as any).browserStorage?.getInteractionInProgress();
        if (activeRequest) {
          console.info("Authentication already in progress, skipping MyProviderData API call");
          return Promise.reject(new Error("Authentication in progress - MyProviderData API call will retry after login"));
        }
      } catch (checkError) {
        // Ignore errors checking interaction state
      }
      
      // Don't initiate auth flow here - let the app-level authentication handle it
      console.info("Deferring MyProviderData authentication to app-level flow");
      return Promise.reject(new Error("Authentication required - handled by app level"));
    }
    
    const tokenRequest = {
      scopes: MY_PROVIDER_DATA_SCOPES,
      account: activeAccount || accounts[0],
    };
    
    try {
      const tokenResponse = await msalApp.acquireTokenSilent(tokenRequest);
      config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return config;
    } catch (error: any) {
      console.error("Error acquiring token silently for MyProviderData service:", error);
      
      // Handle specific MSAL errors that require interaction
      if (isInteractionRequiredError(error)) {
        console.info("Interactive authentication required for MyProviderData. Checking for ongoing interactions...");
        
        // Check if interaction is already in progress
        try {
          const activeRequest = (msalApp as any).browserStorage?.getInteractionInProgress();
          if (activeRequest) {
            console.info("Authentication interaction already in progress, skipping MyProviderData token request");
            return Promise.reject(new Error("Authentication in progress - MyProviderData token will be acquired after login"));
          }
        } catch (checkError) {
          // Ignore errors checking interaction state
        }
        
        // Don't initiate auth flow here - let the app-level authentication handle it
        console.info("Deferring MyProviderData interactive authentication to app-level flow");
        return Promise.reject(new Error("Interactive authentication required - handled by app level"));
      }
      
      return Promise.reject(error);
    }
  });
  
  // Add response interceptor for 401 handling
  myProviderDataInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Bridge mode: the host-delivered token was rejected. Ask the host for
        // a fresh one and retry the request once.
        if (isBridgeActive() && !error.config?.__bridgeRetried) {
          invalidateBridgeToken();
          const freshToken = await getBridgeToken();
          if (freshToken) {
            const retryConfig = { ...error.config, __bridgeRetried: true };
            retryConfig.headers = { ...retryConfig.headers, Authorization: `Bearer ${freshToken}` };
            return myProviderDataInstance(retryConfig);
          }
        }
        console.warn("Received 401 Unauthorized response from MyProviderData service. Deferring to app-level authentication.");
        // Don't initiate login here - let the app handle it
      }
      return Promise.reject(error);
    }
  );
};